/**
 * Admin Members Management - Olympic Fit Wellness
 * Full CRUD: Add, Edit, Suspend, Delete Users
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Plus,
  Search,
  MoreVertical,
  Mail,
  Phone,
  AlertCircle,
  Check,
  X,
  Loader2,
  UserPlus,
  Pause,
  Play,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../../lib/api';

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'member',
    membership_tier: 'free',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const loadMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getAdminUsers();
      setMembers(data || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const filteredMembers = members.filter(member => {
    if (filterTier !== 'all' && member.membership_tier !== filterTier) return false;
    if (filterStatus !== 'all' && (member.status || 'active') !== filterStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (member.full_name?.toLowerCase() || '').includes(query) ||
             (member.email?.toLowerCase() || '').includes(query);
    }
    return true;
  });

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'elite': return 'bg-yellow-500/20 text-yellow-400';
      case 'wellness': return 'bg-blue-500/20 text-blue-400';
      case 'essentials': return 'bg-green-500/20 text-green-400';
      default: return 'bg-neutral-600 text-neutral-300';
    }
  };

  const getRoleBadge = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-500/20 text-red-400';
      case 'founder': return 'bg-purple-500/20 text-purple-400';
      case 'super_admin': return 'bg-purple-500/20 text-purple-400';
      case 'manager': return 'bg-orange-500/20 text-orange-400';
      case 'trainer': return 'bg-blue-500/20 text-blue-400';
      case 'staff': return 'bg-cyan-500/20 text-cyan-400';
      default: return 'bg-neutral-600 text-neutral-300';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'suspended': return 'bg-red-500/20 text-red-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-green-500/20 text-green-400';
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      setMembers(members.map(m => 
        m.id === userId ? { ...m, role: newRole } : m
      ));
      setSuccess('Role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await api.createUser(newUser);
      setSuccess('User created successfully!');
      setShowAddModal(false);
      setNewUser({ email: '', full_name: '', password: '', role: 'member', membership_tier: 'free', phone: '' });
      loadMembers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!showSuspendModal) return;
    setIsSubmitting(true);
    try {
      await api.suspendUser(showSuspendModal.id, suspendReason);
      setSuccess('User suspended successfully');
      setShowSuspendModal(null);
      setSuspendReason('');
      loadMembers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to suspend user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await api.activateUser(userId);
      setSuccess('User activated successfully');
      loadMembers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to activate user');
    }
  };

  const handleDeleteUser = async () => {
    if (!showDeleteModal) return;
    setIsSubmitting(true);
    try {
      await api.deleteUser(showDeleteModal.id);
      setSuccess('User deleted permanently');
      setShowDeleteModal(null);
      loadMembers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Members</h1>
          <p className="text-neutral-400">Manage member accounts and memberships</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors"
          data-testid="add-member-btn"
        >
          <Plus className="w-4 h-4" />
          ADD MEMBER
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-neutral-800 rounded-xl p-4 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          className="px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
        >
          <option value="all">All Tiers</option>
          <option value="elite">Elite</option>
          <option value="wellness">Wellness</option>
          <option value="essentials">Essentials</option>
          <option value="free">Free</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Members Table */}
      <div className="bg-neutral-800 rounded-xl border border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Member</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Membership</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Role</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Joined</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-neutral-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-neutral-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {member.full_name?.[0] || member.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="font-medium text-white">{member.full_name || 'No name'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-neutral-300 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {member.email}
                        </div>
                        {member.phone && (
                          <div className="text-neutral-500 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {member.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getTierBadge(member.membership_tier)}`}>
                        {(member.membership_tier || 'FREE').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={member.role || 'member'}
                        onChange={(e) => updateRole(member.id, e.target.value)}
                        className={`px-2 py-1 text-xs font-medium rounded bg-transparent border border-neutral-600 cursor-pointer ${getRoleBadge(member.role)}`}
                      >
                        <option value="member">Member</option>
                        <option value="trainer">Trainer</option>
                        <option value="staff">Staff</option>
                        <option value="cms_admin">CMS Admin</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(member.status)}`}>
                        {(member.status || 'active').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-neutral-400 text-sm">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(member.status || 'active') === 'active' ? (
                          <button 
                            onClick={() => setShowSuspendModal(member)}
                            className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors"
                            title="Suspend User"
                          >
                            <Pause className="w-4 h-4 text-yellow-400" />
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleActivateUser(member.id)}
                            className="p-2 hover:bg-green-500/20 rounded-lg transition-colors"
                            title="Activate User"
                          >
                            <Play className="w-4 h-4 text-green-400" />
                          </button>
                        )}
                        <button 
                          onClick={() => setShowDeleteModal(member)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-neutral-400">
                    No members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-700">
          <p className="text-sm text-neutral-400">
            Showing {filteredMembers.length} of {members.length} members
          </p>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-md w-full border border-neutral-700">
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h3 className="text-xl font-heading font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#3B82F6]" />
                Add New Member
              </h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white pr-12 focus:outline-none focus:border-[#3B82F6]"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="member">Member</option>
                    <option value="trainer">Trainer</option>
                    <option value="staff">Staff</option>
                    <option value="cms_admin">CMS Admin</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Membership</label>
                  <select
                    value={newUser.membership_tier}
                    onChange={(e) => setNewUser({...newUser, membership_tier: e.target.value})}
                    className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="free">Free</option>
                    <option value="essentials">Essentials</option>
                    <option value="wellness">Wellness</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-6 py-3 border border-neutral-600 text-neutral-300 rounded-lg hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-[#3B82F6] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-md w-full border border-neutral-700">
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h3 className="text-xl font-heading font-bold text-white">Suspend User</h3>
              <button onClick={() => setShowSuspendModal(null)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-neutral-300">
                Are you sure you want to suspend <strong className="text-white">{showSuspendModal.full_name || showSuspendModal.email}</strong>?
              </p>
              <p className="text-sm text-neutral-400">
                This will prevent them from logging in until reactivated.
              </p>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Reason (optional)</label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension..."
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white resize-none focus:outline-none focus:border-[#3B82F6]"
                  rows={3}
                />
              </div>
              <div className="flex gap-4 pt-2">
                <button
                  onClick={() => setShowSuspendModal(null)}
                  className="flex-1 px-6 py-3 border border-neutral-600 text-neutral-300 rounded-lg hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSuspendUser}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Suspending...' : 'Suspend User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-md w-full border border-neutral-700">
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h3 className="text-xl font-heading font-bold text-red-400">Delete User</h3>
              <button onClick={() => setShowDeleteModal(null)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 font-medium">Warning: This action cannot be undone!</p>
              </div>
              <p className="text-neutral-300">
                Are you sure you want to permanently delete <strong className="text-white">{showDeleteModal.full_name || showDeleteModal.email}</strong>?
              </p>
              <p className="text-sm text-neutral-400">
                All their data, bookings, and workout history will be removed.
              </p>
              <div className="flex gap-4 pt-2">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 px-6 py-3 border border-neutral-600 text-neutral-300 rounded-lg hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
